import * as React from 'react';
import { Box, Grid, ThemeProvider, Typography } from '@mui/material';
import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { generateTheme } from '../themes';

const MOBILE_TITLE_SIZES = {
    s: { xs: '1.375rem', sm: '1.75rem' },
    m: { xs: '1.75rem', sm: '2.25rem' },
    l: { xs: '2rem', sm: '2.75rem' }
};

const DESKTOP_TITLE_SIZES = {
    s: { md: '2.25rem', lg: '2.5rem' },
    m: { md: '2.75rem', lg: '3.25rem' },
    l: { md: '3.5rem', lg: '4rem' },
    xl: { md: '4.25rem', lg: '5rem' }
};

const titleFontSize = (mobile, desktop) => ({
    ...(MOBILE_TITLE_SIZES[mobile] || MOBILE_TITLE_SIZES.m),
    ...(DESKTOP_TITLE_SIZES[desktop] || DESKTOP_TITLE_SIZES.l)
});

const PADDING_MAP = {
    'None':   { xs: '0px',  md: '0px' },
    'Small':  { xs: '24px', md: '48px' },
    'Normal': { xs: '48px', md: '80px' },
    'Large':  { xs: '72px', md: '120px' }
};

const DEFAULT_PAD = { xs: '48px', md: '80px' };

const resolvePadding = (value, bp) => {
    if (!value || value === 'Auto') return DEFAULT_PAD[bp];
    return PADDING_MAP[value]?.[bp] ?? DEFAULT_PAD[bp];
};


const FeaturesGridSlice = ({ data }) => {
    const {
        colorTheme = 'off-white',
        columns = '2',
        eyebrow,
        title,
        body,
        categories = [],
        gradientHeadline = false,
        centerAlign = false,
        titleSizeDesktop = 'l',
        titleSizeMobile = 'm',
        sliceMarginTop = 'Auto',
        sliceMarginTopMobile = 'Auto',
        sliceMarginBottom = 'Auto',
        sliceMarginBottomMobile = 'Auto'
    } = data;

    const theme = generateTheme(colorTheme);
    const containerRef = useRef(null);

    const colSpec = columns === '1'
        ? { xs: 12 }
        : columns === '3'
            ? { xs: 12, sm: 6, lg: 4 }
            : { xs: 12, sm: 6 };

    const slicePt = { xs: resolvePadding(sliceMarginTopMobile, 'xs'), md: resolvePadding(sliceMarginTop, 'md') };
    const slicePb = { xs: resolvePadding(sliceMarginBottomMobile, 'xs'), md: resolvePadding(sliceMarginBottom, 'md') };

    useLayoutEffect(() => {
        if (!containerRef.current) return;
        const targets = gsap.utils.toArray('.fg-animate', containerRef.current);
        targets.forEach((el, i) => {
            gsap.fromTo(
                el,
                { opacity: 0, y: 10 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.6,
                    delay: 0.04 * i,
                    ease: 'easeIn',
                    scrollTrigger: {
                        id: 'fgAnimate',
                        trigger: el,
                        start: 'top 88%',
                        markers: false
                    }
                }
            );
        });
    }, []);

    return (
        <ThemeProvider theme={theme}>
            <Box
                ref={containerRef}
                sx={{
                    background: (t) => t.palette.background.default,
                    px: { xs: 3, md: 12 },
                    pt: slicePt,
                    pb: slicePb
                }}
            >
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                    <Box sx={{ textAlign: centerAlign ? 'center' : 'left' }}>
                        {eyebrow && (
                            <Typography
                                className="fg-animate"
                                component="span"
                                sx={{
                                    display: 'block',
                                    color: 'text.secondary',
                                    letterSpacing: '0.2em',
                                    textTransform: 'uppercase',
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    opacity: 0.7,
                                    mb: { xs: 2, md: 2.5 }
                                }}
                                dangerouslySetInnerHTML={{ __html: eyebrow }}
                            />
                        )}

                        {title && (
                            <Typography
                                className="fg-animate"
                                component="h2"
                                lang="sv"
                                sx={{
                                    color: 'text.primary',
                                    fontFamily: 'Kanit, sans-serif',
                                    fontWeight: 800,
                                    textTransform: 'uppercase',
                                    fontSize: titleFontSize(titleSizeMobile, titleSizeDesktop),
                                    lineHeight: 1.05,
                                    letterSpacing: '-0.01em',
                                    overflowWrap: 'break-word',
                                    hyphens: 'auto',
                                    maxWidth: 820,
                                    mx: centerAlign ? 'auto' : undefined,
                                    mb: body ? 3 : 5,
                                    ...(gradientHeadline ? {
                                        background: 'linear-gradient(90deg, rgb(6, 194, 220) 0%, rgb(0, 158, 187) 50%, rgb(92, 51, 215) 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                        color: 'transparent'
                                    } : {})
                                }}
                                dangerouslySetInnerHTML={{ __html: title }}
                            />
                        )}

                        {body && (
                            <Typography
                                className="fg-animate"
                                component="p"
                                sx={{
                                    color: 'text.secondary',
                                    fontSize: { xs: '1rem', md: '1.125rem' },
                                    lineHeight: 1.6,
                                    maxWidth: 640,
                                    mx: centerAlign ? 'auto' : undefined,
                                    mb: { xs: 5, md: 7 }
                                }}
                                dangerouslySetInnerHTML={{ __html: body }}
                            />
                        )}
                    </Box>

                    <Grid container spacing={{ xs: 4, md: 6 }}>
                        {categories.map((cat, i) => (
                            <Grid
                                item
                                key={i}
                                {...colSpec}
                                className="fg-animate"
                            >
                                {(cat.iconUrl || cat.title) && (
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        pb: 1.5,
                                        mb: 3,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider'
                                    }}>
                                        {cat.iconUrl && (
                                            <Box
                                                component="img"
                                                src={cat.iconUrl}
                                                alt={cat.iconAlt || ''}
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    objectFit: 'contain',
                                                    flexShrink: 0,
                                                    display: 'block'
                                                }}
                                            />
                                        )}
                                        {cat.title && (
                                            <Typography
                                                component="h3"
                                                sx={{
                                                    color: 'text.primary',
                                                    fontFamily: 'Kanit, sans-serif',
                                                    fontSize: { xs: '1rem', md: '1.0625rem' },
                                                    fontWeight: 700,
                                                    letterSpacing: 0,
                                                    lineHeight: 1.2,
                                                    m: 0
                                                }}
                                                dangerouslySetInnerHTML={{ __html: cat.title }}
                                            />
                                        )}
                                    </Box>
                                )}
                                <Box
                                    component="ul"
                                    sx={{
                                        listStyle: 'none',
                                        m: 0,
                                        p: 0,
                                        '& > li': {
                                            position: 'relative',
                                            pl: '2rem',
                                            mb: { xs: 1.75, md: 2.25 },
                                            color: 'text.primary',
                                            fontSize: { xs: '1rem', md: '1.0625rem' },
                                            lineHeight: 1.4
                                        },
                                        '& > li::before': {
                                            content: '"\\2713"',
                                            position: 'absolute',
                                            left: 0,
                                            top: 0,
                                            color: '#0091A5',
                                            fontWeight: 700,
                                            fontSize: '1.05em'
                                        }
                                    }}
                                >
                                    {(cat.items || []).map((item, j) => (
                                        <li key={j} dangerouslySetInnerHTML={{ __html: item }} />
                                    ))}
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default FeaturesGridSlice;
